import Http from "./Http";
import dataService from "./data.service";

/**
 * Lock/Unlock service
 *
 * Thin wrapper over the unified backend Lock APIs:
 *   POST   /locks           — create a lock (LOCK_LINE | LOCK_DRIVER)
 *   DELETE /locks/{id}      — release a lock (soft, sets active=false)
 *   GET    /locks?planId=…  — audit metadata (locker, timestamp, reason)
 *
 * Errors are re-thrown so callers can route them through the existing
 * `handleApiError` (toasts the unified 403 lock-blocked message and 409
 * Conflict messages verbatim).
 *
 * ----------------------------------------------------------------------
 * Create payload (POST /locks)
 *
 *   { planId, lockType, coaId?, lockReason? }
 *
 *   - lockType  "LOCK_LINE" | "LOCK_DRIVER"
 *   - coaId     required for LOCK_LINE / LOCK_DRIVER
 *   - lockReason optional free-text (≤ 1000 chars)
 *
 * Response body carries: id, lockType, planId, coaId, lockedByUser*,
 * lockedAt, lockReason, active. There is no longer a `lockScope` field —
 * the `lockType` IS the scope.
 *
 * ----------------------------------------------------------------------
 * Read-payload lock indicators (slim — no audit fields)
 *
 * Plan list (`POST /plan/search`) row:
 *   - `locked`     boolean   — true if any active lock (FC-level)
 *
 * Plan header (`GET /plan/header-data/{id}`):
 *   - `locked` at the top level (siblings of `plan`/`versions`).
 *
 * Line-item rows (PlanDetailDTO from
 *   GET  /plan-details/search/{planId},
 *   POST /plan-details/{planId},
 *   POST /plan-details/income-statement/{planId}):
 *   - `lockType`  "LOCK_LINE" | "LOCK_DRIVER" | null
 *   - `lockId`    Long? — id of the lock for this row (null when unlocked)
 *   - `lockedByUserName`, `lockedByUserEmail` — locker identity when active lock
 *
 * A row can hold at most one lock at a time (either LOCK_LINE OR
 * LOCK_DRIVER). LOCK_LINE blocks both value and driver edits; LOCK_DRIVER
 * blocks only driver edits.
 * ----------------------------------------------------------------------
 */

export const LOCK_TYPE = Object.freeze({
  LOCK_LINE: "LOCK_LINE",
  LOCK_DRIVER: "LOCK_DRIVER",
});

export const LOCK_TYPE_LABEL = Object.freeze({
  [LOCK_TYPE.LOCK_LINE]: "Line",
  [LOCK_TYPE.LOCK_DRIVER]: "Driver",
});

const LOCK_REASON_MAX_LENGTH = 1000;
export const LOCK_REASON_MAX = LOCK_REASON_MAX_LENGTH;

/**
 * Create a new active lock.
 *
 * @param {Object} params
 * @param {number|string} params.planId
 * @param {"LOCK_LINE"|"LOCK_DRIVER"} params.lockType
 * @param {number|string} params.coaId — required
 * @param {string} [params.lockReason]   — free-text note (≤ 1000 chars)
 * @returns {Promise<Object>} created lock object
 */
export const createLock = async ({
  planId,
  lockType,
  coaId = null,
  lockReason,
}) => {
  if (coaId == null) {
    throw new Error("createLock requires coaId");
  }
  const payload = {
    planId,
    lockType,
    coaId,
  };
  if (lockReason != null && String(lockReason).trim() !== "") {
    payload.lockReason = String(lockReason).slice(0, LOCK_REASON_MAX_LENGTH);
  }
  const response = await Http.POST("locks", payload);
  return response?.data;
};

/** Convenience helper — lock a single COA's value. */
export const createValueLock = ({ planId, coaId, lockReason }) =>
  createLock({ planId, lockType: LOCK_TYPE.LOCK_LINE, coaId, lockReason });

/** Convenience helper — lock a single COA's driver. */
export const createDriverLock = ({ planId, coaId, lockReason }) =>
  createLock({ planId, lockType: LOCK_TYPE.LOCK_DRIVER, coaId, lockReason });

/**
 * Soft-release an active lock by id.
 * @param {number|string} lockId
 * @returns {Promise<Object>} updated lock object (active=false)
 */
export const releaseLock = async (lockId) => {
  if (lockId == null) {
    throw new Error("releaseLock requires a lock id");
  }
  const response = await Http.DELETE("locks/" + encodeURIComponent(lockId));
  return response?.data;
};

/**
 * Why a line in a bulk lock/unlock selection was not acted on.
 * Mirrors the backend `BulkLockSkipReason` enum (TPX-2419).
 */
export const BULK_LOCK_SKIP_REASON = Object.freeze({
  NO_PERMISSION: "NO_PERMISSION",
  ALREADY_LOCKED: "ALREADY_LOCKED",
  NOT_LOCKED: "NOT_LOCKED",
});

const buildBulkPayload = ({ planId, lockType, coaIds }) => {
  if (planId == null) {
    throw new Error("bulk lock requires planId");
  }
  if (!lockType) {
    throw new Error("bulk lock requires lockType");
  }
  const ids = Array.from(
    new Set((coaIds || []).filter((id) => id != null).map((id) => Number(id)))
  );
  if (ids.length === 0) {
    throw new Error("bulk lock requires at least one coaId");
  }
  return { planId, lockType, coaIds: ids };
};

/**
 * Lock a whole selection of line items in one call.
 *
 * Partial success: the response reports which lines were applied and which were
 * skipped, so a 200 can still come back with nothing applied.
 *
 * @param {Object} params
 * @param {number|string} params.planId
 * @param {"LOCK_LINE"|"LOCK_DRIVER"} params.lockType
 * @param {Array<number|string>} params.coaIds
 * @param {string} [params.lockReason]
 * @returns {Promise<{applied: Object[], skipped: Object[], appliedCount: number, skippedCount: number}>}
 */
export const bulkLock = async ({ planId, lockType, coaIds, lockReason }) => {
  const payload = buildBulkPayload({ planId, lockType, coaIds });
  if (lockReason != null && String(lockReason).trim() !== "") {
    payload.lockReason = String(lockReason).slice(0, LOCK_REASON_MAX_LENGTH);
  }
  const response = await Http.POST("locks/bulk", payload);
  return response?.data;
};

/**
 * Release the active lock of the given type across a selection of line items.
 * Works by coaIds — the caller does not need each row's lock id.
 */
export const bulkUnlock = async ({ planId, lockType, coaIds }) => {
  const response = await Http.POST(
    "locks/bulk/unlock",
    buildBulkPayload({ planId, lockType, coaIds })
  );
  return response?.data;
};

/** When the user lacks TPX permission to change lock state (scoped). */
export const LOCK_PERMISSION_DENIED = Object.freeze({
  plan: "You do not have permission to lock/unlock plan.",
  driver: "You do not have permission to lock/unlock driver.",
  line: "You do not have permission to lock/unlock line.",
});

/**
 * Hover copy for an active FC/plan detail lock when the API sends locker identity.
 * @param {{ lockedByUserName?: string, lockedByUserEmail?: string } | null | undefined} source
 * @param {"plan"|"driver"|"line"} scope
 */
export const formatActiveLockContactTooltip = (source, scope) => {
  const rawName = source?.lockedByUserName ?? source?.lockedByUserEmail ?? "";
  const trimmed = typeof rawName === "string" ? rawName.trim() : "";
  const displayName = trimmed !== "" ? trimmed : "another user";
  const lockerEmailRaw = source?.lockedByUserEmail ?? "";
  const lockerEmail = typeof lockerEmailRaw === "string" ? lockerEmailRaw.trim().toLowerCase() : "";
  const currentUserEmailRaw = dataService?.getUserEmail?.() ?? "";
  const currentUserEmail =
    typeof currentUserEmailRaw === "string" ? currentUserEmailRaw.trim().toLowerCase() : "";
  const noun =
    scope === "plan" ? "plan" : scope === "driver" ? "driver" : "line";
  if (lockerEmail && currentUserEmail && lockerEmail === currentUserEmail) {
    return `Locked by you. Unlock to edit this ${noun}.`;
  }
  return `Locked by ${displayName}. Contact them to change ${noun}.`;
};

/** Pretty label for a lock object (for tooltips / dialogs). */
export const formatLockOwnerLabel = (lock) => {
  if (!lock) return "";
  const name = lock.lockedByUserName || lock.lockedByUserEmail || "another user";
  return "Locked by " + name;
};

export default {
  LOCK_TYPE,
  LOCK_TYPE_LABEL,
  LOCK_PERMISSION_DENIED,
  LOCK_REASON_MAX,
  BULK_LOCK_SKIP_REASON,
  createLock,
  createValueLock,
  createDriverLock,
  releaseLock,
  bulkLock,
  bulkUnlock,
  formatActiveLockContactTooltip,
  formatLockOwnerLabel,
};
